import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import RegistrationFilterPanel from './components/RegistrationFilterPanel';
import RegistrationTable from './components/RegistrationTable';
import RegistrationDetailModal from './components/RegistrationDetailModal';
import BulkApprovalBar from './components/BulkApprovalBar';

const RegistrationManagementApprovalCenter = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    registrationType: 'all',
    verificationStatus: 'all',
    dateRange: { start: '', end: '' },
    searchQuery: ''
  });
  const [selectedRegistrations, setSelectedRegistrations] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState('table');

  const tabs = [
    { id: 'all', label: 'All Registrations', count: 24 },
    { id: 'pending', label: 'Pending Approvals', count: 8 },
    { id: 'verification', label: 'Verification Status', count: 5 }
  ];

  const mockRegistrations = [
    {
      id: 1,
      applicantName: 'Rahul Sharma',
      registrationType: 'Student',
      email: 'rahul.sharma@example.com',
      phone: '+91 98765 43210',
      submissionDate: '2026-02-01T10:30:00',
      verificationStatus: 'Verified',
      approvalStatus: 'Pending',
      institution: 'Mumbai Institute of Technology',
      course: 'B.Tech Computer Science',
      documents: ['ID Proof', 'Academic Records', 'Photo'],
      priority: 'normal',
      notes: ''
    },
    {
      id: 2,
      applicantName: 'TechCorp Solutions',
      registrationType: 'Employer',
      email: 'hr@techcorp.com',
      phone: '+91 98765 43211',
      submissionDate: '2026-01-30T14:20:00',
      verificationStatus: 'Pending',
      approvalStatus: 'Pending',
      companySize: '50-200 employees',
      industry: 'Information Technology',
      documents: ['Company Registration', 'GST Certificate', 'PAN Card'],
      priority: 'high',
      notes: 'Urgent - Large hiring requirement'
    },
    {
      id: 3,
      applicantName: 'Priya Patel',
      registrationType: 'Student',
      email: 'priya.patel@example.com',
      phone: '+91 98765 43212',
      submissionDate: '2026-01-28T09:15:00',
      verificationStatus: 'Verified',
      approvalStatus: 'Approved',
      institution: 'Delhi University',
      course: 'MCA',
      documents: ['ID Proof', 'Academic Records', 'Photo'],
      priority: 'normal',
      notes: ''
    },
    {
      id: 4,
      applicantName: 'Bangalore Engineering College',
      registrationType: 'Institution',
      email: 'admin@bec.edu.in',
      phone: '+91 98765 43213',
      submissionDate: '2026-01-25T11:45:00',
      verificationStatus: 'Under Review',
      approvalStatus: 'Pending',
      institutionType: 'Engineering College',
      accreditation: 'NAAC A+ Grade',
      documents: ['Accreditation Certificate', 'Registration Proof', 'Authorization Letter'],
      priority: 'high',
      notes: 'Requires additional verification'
    },
    {
      id: 5,
      applicantName: 'Amit Kumar',
      registrationType: 'Student',
      email: 'amit.kumar@example.com',
      phone: '+91 98765 43214',
      submissionDate: '2026-01-22T16:30:00',
      verificationStatus: 'Rejected',
      approvalStatus: 'Rejected',
      institution: 'Pune Institute of Technology',
      course: 'B.Tech IT',
      documents: ['ID Proof', 'Academic Records'],
      priority: 'normal',
      notes: 'Incomplete documents'
    },
    {
      id: 6,
      applicantName: 'InnovateTech Pvt Ltd',
      registrationType: 'Employer',
      email: 'contact@innovatetech.com',
      phone: '+91 98765 43215',
      submissionDate: '2026-02-02T08:00:00',
      verificationStatus: 'Verified',
      approvalStatus: 'Approved',
      companySize: '200-500 employees',
      industry: 'Software Development',
      documents: ['Company Registration', 'GST Certificate', 'PAN Card', 'Office Address Proof'],
      priority: 'normal',
      notes: ''
    },
    {
      id: 7,
      applicantName: 'Sneha Reddy',
      registrationType: 'Student',
      email: 'sneha.reddy@example.com',
      phone: '+91 98765 43216',
      submissionDate: '2026-01-20T13:25:00',
      verificationStatus: 'Pending',
      approvalStatus: 'Pending',
      institution: 'Hyderabad Institute of Technology',
      course: 'B.Sc Computer Science',
      documents: ['ID Proof', 'Academic Records', 'Photo'],
      priority: 'normal',
      notes: ''
    },
    {
      id: 8,
      applicantName: 'Chennai Technical University',
      registrationType: 'Institution',
      email: 'registrar@ctu.edu.in',
      phone: '+91 98765 43217',
      submissionDate: '2026-01-18T10:00:00',
      verificationStatus: 'Verified',
      approvalStatus: 'Approved',
      institutionType: 'University',
      accreditation: 'UGC Approved',
      documents: ['Accreditation Certificate', 'Registration Proof', 'Authorization Letter', 'Infrastructure Details'],
      priority: 'normal',
      notes: ''
    }
  ];

  const filteredRegistrations = mockRegistrations?.filter((reg) => {
    if (activeTab === 'pending' && reg?.approvalStatus !== 'Pending') return false;
    if (activeTab === 'verification' && reg?.verificationStatus === 'Verified') return false;
    
    if (filters?.registrationType !== 'all' && reg?.registrationType !== filters?.registrationType) return false;
    if (filters?.verificationStatus !== 'all' && reg?.verificationStatus !== filters?.verificationStatus) return false;
    
    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      const matchesName = reg?.applicantName?.toLowerCase()?.includes(query);
      const matchesEmail = reg?.email?.toLowerCase()?.includes(query);
      const matchesType = reg?.registrationType?.toLowerCase()?.includes(query);
      if (!matchesName && !matchesEmail && !matchesType) return false;
    }
    
    if (filters?.dateRange?.start) {
      const regDate = new Date(reg?.submissionDate);
      const startDate = new Date(filters?.dateRange?.start);
      if (regDate < startDate) return false;
    }
    
    if (filters?.dateRange?.end) {
      const regDate = new Date(reg?.submissionDate);
      const endDate = new Date(filters?.dateRange?.end);
      if (regDate > endDate) return false;
    }
    
    return true;
  });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      registrationType: 'all',
      verificationStatus: 'all',
      dateRange: { start: '', end: '' },
      searchQuery: ''
    });
  };

  const handleSelectRegistration = (registrationId) => {
    setSelectedRegistrations((prev) =>
      prev?.includes(registrationId)
        ? prev?.filter((id) => id !== registrationId)
        : [...prev, registrationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRegistrations?.length === filteredRegistrations?.length) {
      setSelectedRegistrations([]);
    } else {
      setSelectedRegistrations(filteredRegistrations?.map((r) => r?.id));
    }
  };

  const handleApprove = (registrationId) => {
    console.log(`Approving registration ${registrationId}`);
    alert(`Registration approved successfully!`);
  };

  const handleReject = (registrationId) => {
    console.log(`Rejecting registration ${registrationId}`);
    alert(`Registration rejected.`);
  };

  const handleRequestInfo = (registrationId) => {
    console.log(`Requesting additional info for ${registrationId}`);
    alert(`Additional information request sent.`);
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk ${action} for:`, selectedRegistrations);
    alert(`Bulk ${action} completed for ${selectedRegistrations?.length} registrations.`);
    setSelectedRegistrations([]);
  };

  const getTabCount = (tabId) => {
    const tab = tabs?.find(t => t?.id === tabId);
    return tab?.count || 0;
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
              Registration Management & Approval Center
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Review, approve, and manage all platform registrations
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              iconName="Filter"
              iconPosition="left"
              className="lg:hidden"
            >
              Filters
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('table')}
            >
              <Icon name="List" size={18} />
            </Button>
            <Button
              variant={viewMode === 'card' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('card')}
            >
              <Icon name="LayoutGrid" size={18} />
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg">
          <div className="border-b border-border overflow-x-auto">
            <div className="flex">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-medium whitespace-nowrap transition-smooth ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {tab?.label}
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {tab?.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className={`lg:w-64 flex-shrink-0 ${isFilterOpen ? 'block' : 'hidden lg:block'}`}>
                <RegistrationFilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  resultCount={filteredRegistrations?.length}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="mb-4">
                  <Input
                    type="text"
                    placeholder="Search by name, email, or type..."
                    value={filters?.searchQuery}
                    onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
                    className="w-full"
                  />
                </div>

                {selectedRegistrations?.length > 0 && (
                  <BulkApprovalBar
                    selectedCount={selectedRegistrations?.length}
                    onBulkAction={handleBulkAction}
                    onClearSelection={() => setSelectedRegistrations([])}
                  />
                )}

                <RegistrationTable
                  registrations={filteredRegistrations}
                  selectedRegistrations={selectedRegistrations}
                  onSelectRegistration={handleSelectRegistration}
                  onSelectAll={handleSelectAll}
                  onViewDetails={(reg) => setSelectedRegistration(reg)}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onRequestInfo={handleRequestInfo}
                  viewMode={viewMode}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedRegistration && (
        <RegistrationDetailModal
          registration={selectedRegistration}
          onClose={() => setSelectedRegistration(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onRequestInfo={handleRequestInfo}
        />
      )}
    </MainLayout>
  );
};

export default RegistrationManagementApprovalCenter;